import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MessageCircle, Phone, Calendar, Gauge, Fuel, Cog, Zap, MapPin, Calculator, Bookmark, ShieldCheck, CheckCircle2, ChevronLeft, ChevronRight, X } from "lucide-react";
import api, { resolveImageUrl } from "../lib/api";
import VehicleCard from "../components/VehicleCard";
import FinancingCalculator from "../components/FinancingCalculator";
import ReservationDialog from "../components/ReservationDialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { buildWhatsappUrl, openWhatsapp, WA_MESSAGES, phoneHref, formatEUR, formatKM } from "../lib/contact";
import { toast } from "sonner";

const VehicleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = React.useState(null);
  const [similar, setSimilar] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [activeImage, setActiveImage] = React.useState(0);
  const [lightboxOpen, setLightboxOpen] = React.useState(false);

  const [testDriveOpen, setTestDriveOpen] = React.useState(false);
  const [testForm, setTestForm] = React.useState({ name: "", phone: "", email: "", preferred_date: "", message: "" });
  const [submittingTd, setSubmittingTd] = React.useState(false);
  const [reservationOpen, setReservationOpen] = React.useState(false);

  React.useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/vehicles/${id}`),
      api.get(`/vehicles/${id}/similar`),
    ])
      .then(([v, s]) => { setVehicle(v.data); setSimilar(s.data || []); setActiveImage(0); })
      .catch(() => setVehicle(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="aspect-[16/9] bg-[#232330] rounded-2xl animate-pulse" />
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <h2 className="font-display text-3xl text-white">Fahrzeug nicht gefunden</h2>
        <Link to="/fahrzeuge" data-testid="detail-back-to-list" className="inline-flex items-center gap-2 mt-6 text-zinc-400 hover:text-white">
          <ArrowLeft className="w-4 h-4" /> Zurück zur Übersicht
        </Link>
      </div>
    );
  }

  const name = `${vehicle.make} ${vehicle.model}`;
  const images = vehicle.images?.length ? vehicle.images : ["https://images.unsplash.com/photo-1494976388531-d1058494cdd8?auto=format&fit=crop&w=1600&q=80"];

  const submitTestDrive = async (e) => {
    e.preventDefault();
    if (!testForm.name || !testForm.phone) {
      toast.error("Bitte Name und Telefon ausfüllen.");
      return;
    }
    setSubmittingTd(true);
    try {
      await api.post("/test-drives", { vehicle_id: vehicle.id, vehicle_name: name, ...testForm });
      toast.success("Probefahrt-Anfrage gesendet. Wir melden uns!");
      setTestDriveOpen(false);
      setTestForm({ name: "", phone: "", email: "", preferred_date: "", message: "" });
    } catch (err) {
      toast.error("Senden fehlgeschlagen.");
    } finally {
      setSubmittingTd(false);
    }
  };

  const SpecRow = ({ icon: Icon, label, value }) => (
    <div className="flex items-center justify-between py-3 border-b border-white/5">
      <span className="inline-flex items-center gap-2 text-zinc-400 text-sm">
        <Icon className="w-4 h-4 text-zinc-500" /> {label}
      </span>
      <span className="text-white text-sm font-medium">{value}</span>
    </div>
  );

  return (
    <div className="bg-[var(--na-bg)] pt-8 lg:pt-12 pb-16" data-testid="vehicle-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate(-1)}
          data-testid="detail-back-button"
          className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Zurück
        </button>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Gallery */}
          <div className="lg:col-span-7">
            <div
              className="aspect-[16/10] w-full rounded-2xl overflow-hidden bg-[#2E2E3C] border border-silver-soft cursor-zoom-in relative"
              onClick={() => setLightboxOpen(true)}
              data-testid="detail-main-image"
            >
              <img src={resolveImageUrl(images[activeImage])} alt={name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none" />
              {vehicle.badge && (
                <span className="absolute top-4 left-4 text-[11px] uppercase tracking-[0.18em] font-semibold px-3 py-1.5 bg-[var(--na-red)] text-white rounded-xl">
                  {vehicle.badge}
                </span>
              )}
            </div>
            {images.length > 1 && (
              <div className="grid grid-cols-5 gap-2 mt-2">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImage(i)}
                    data-testid={`detail-thumbnail-${i}`}
                    className={`aspect-[16/10] rounded-xl overflow-hidden border ${i === activeImage ? "border-[var(--na-red)]" : "border-white/5 hover:border-white/20"} transition-colors`}
                  >
                    <img src={resolveImageUrl(img)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info / Sticky CTAs */}
          <aside className="lg:col-span-5 lg:sticky lg:top-24 self-start">
            <p className="text-[11px] uppercase tracking-[0.28em] text-zinc-500">{vehicle.make}</p>
            <h1 className="font-display text-3xl lg:text-4xl xl:text-5xl font-bold text-white tracking-tight leading-[1.05] mt-2">
              {vehicle.model}
            </h1>
            <div className="mt-4 flex items-baseline gap-3">
              <p className="font-display text-4xl lg:text-5xl font-bold text-white tracking-tighter">
                {formatEUR(vehicle.price)}
              </p>
              <p className="text-xs uppercase tracking-widest text-zinc-500">inkl. 20% USt.</p>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-y-2 text-sm">
              <SpecRow icon={Calendar} label="Baujahr" value={vehicle.year} />
              <SpecRow icon={Gauge} label="Kilometer" value={formatKM(vehicle.mileage)} />
              <SpecRow icon={Fuel} label="Kraftstoff" value={vehicle.fuel} />
              <SpecRow icon={Cog} label="Getriebe" value={vehicle.transmission} />
              <SpecRow icon={Zap} label="Leistung" value={`${vehicle.power_hp} PS`} />
              <SpecRow icon={MapPin} label="Standort" value={vehicle.location} />
            </div>

            <div className="mt-6 space-y-3">
              <a
                href={buildWhatsappUrl(WA_MESSAGES.vehicleDetail(name))}
                onClick={openWhatsapp(WA_MESSAGES.vehicleDetail(name))}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="detail-whatsapp-link"
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-[#25D366] text-[#1A1A22] font-medium rounded-xl hover:bg-[#20bd5a] transition-colors"
              >
                <MessageCircle className="w-4 h-4" /> Per WhatsApp anfragen
              </a>
              <div className="grid grid-cols-2 gap-3">
                <a
                  href={phoneHref}
                  data-testid="detail-phone-link"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-white text-[#1A1A22] rounded-xl font-medium hover:bg-zinc-200 transition-colors"
                >
                  <Phone className="w-4 h-4" /> Anrufen
                </a>
                <button
                  onClick={() => setTestDriveOpen(true)}
                  data-testid="detail-test-drive-button"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-white/20 hover:bg-white/5 text-white rounded-xl font-medium transition-colors"
                >
                  <Bookmark className="w-4 h-4" /> Probefahrt
                </button>
              </div>
              <button
                onClick={() => setReservationOpen(true)}
                data-testid="detail-reserve-button"
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 border border-[var(--na-red)] text-white hover:bg-[var(--na-red)]/10 rounded-xl font-medium transition-colors"
              >
                Fahrzeug reservieren · €200
              </button>
            </div>

            <div className="mt-6 p-4 rounded-xl bg-[#2E2E3C] border border-silver-soft text-xs text-zinc-400 leading-relaxed flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-[var(--na-red)] mt-0.5 flex-shrink-0" />
              Garantie und Finanzierung auf Wunsch verfügbar. Lückenlose Servicehistorie auf Anfrage.
            </div>
          </aside>
        </div>

        {/* Description */}
        {vehicle.description && (
          <section className="mt-16 lg:mt-24 grid lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-7">
              <h2 className="font-display text-2xl lg:text-3xl font-bold text-white tracking-tight mb-5">
                Beschreibung
              </h2>
              <p className="text-zinc-300 leading-relaxed">{vehicle.description}</p>
            </div>
            <div className="lg:col-span-5">
              <h3 className="font-display text-2xl lg:text-3xl font-bold text-white tracking-tight mb-5">Ausstattung</h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-sm text-zinc-300">
                {vehicle.equipment?.map((e) => (
                  <li key={e} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[var(--na-red)] mt-0.5 flex-shrink-0" /> {e}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* Financing block */}
        <section className="mt-16 lg:mt-24">
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-white tracking-tighter mb-2">
            Finanzierungsbeispiel
          </h2>
          <p className="text-zinc-400 mb-8 max-w-2xl">
            Berechnen Sie Ihre individuelle Rate für dieses Fahrzeug und senden Sie uns direkt eine Anfrage.
          </p>
          <FinancingCalculator initialVehicle={vehicle} />
        </section>

        {/* Similar vehicles */}
        {similar.length > 0 && (
          <section className="mt-16 lg:mt-24">
            <div className="flex items-baseline justify-between mb-8">
              <h2 className="font-display text-3xl lg:text-4xl font-bold text-white tracking-tighter">
                Ähnliche Fahrzeuge
              </h2>
              <Link to="/fahrzeuge" data-testid="detail-similar-link" className="text-sm text-zinc-400 hover:text-white">Alle ansehen →</Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {similar.map((v, i) => <VehicleCard key={v.id} vehicle={v} index={i} />)}
            </div>
          </section>
        )}
      </div>

      {/* Reservation Dialog */}
      <ReservationDialog open={reservationOpen} onOpenChange={setReservationOpen} vehicle={vehicle} />

      {/* Test Drive Dialog */}
      <Dialog open={testDriveOpen} onOpenChange={setTestDriveOpen}>
        <DialogContent className="bg-[#232330] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Probefahrt anfragen</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitTestDrive} className="space-y-4">
            <p className="text-sm text-zinc-400">Für: <span className="text-white">{name}</span></p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-zinc-400 mb-1.5 block">Name *</Label>
                <Input data-testid="testdrive-name" value={testForm.name} onChange={(e) => setTestForm({ ...testForm, name: e.target.value })} className="bg-[#1A1A22] border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-zinc-400 mb-1.5 block">Telefon *</Label>
                <Input data-testid="testdrive-phone" value={testForm.phone} onChange={(e) => setTestForm({ ...testForm, phone: e.target.value })} className="bg-[#1A1A22] border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-zinc-400 mb-1.5 block">E-Mail</Label>
                <Input data-testid="testdrive-email" type="email" value={testForm.email} onChange={(e) => setTestForm({ ...testForm, email: e.target.value })} className="bg-[#1A1A22] border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-zinc-400 mb-1.5 block">Wunschtermin</Label>
                <Input data-testid="testdrive-date" type="date" value={testForm.preferred_date} onChange={(e) => setTestForm({ ...testForm, preferred_date: e.target.value })} className="bg-[#1A1A22] border-white/10 text-white" />
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-zinc-400 mb-1.5 block">Nachricht</Label>
              <textarea data-testid="testdrive-message" rows={3} value={testForm.message} onChange={(e) => setTestForm({ ...testForm, message: e.target.value })} className="w-full bg-[#1A1A22] border border-white/10 text-white rounded-xl px-3 py-2 text-sm" />
            </div>
            <Button type="submit" disabled={submittingTd} data-testid="testdrive-submit" className="w-full bg-[var(--na-red)] hover:bg-[var(--na-red-hover)] text-white rounded-xl">
              {submittingTd ? "Wird gesendet…" : "Anfrage senden"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Lightbox */}
      {lightboxOpen && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center" onClick={() => setLightboxOpen(false)}>
          <button onClick={() => setLightboxOpen(false)} className="absolute top-6 right-6 text-white p-2 hover:bg-white/10 rounded-xl" data-testid="lightbox-close">
            <X className="w-6 h-6" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); setActiveImage((i) => (i - 1 + images.length) % images.length); }} className="absolute left-4 lg:left-8 text-white p-3 hover:bg-white/10 rounded-xl">
            <ChevronLeft className="w-7 h-7" />
          </button>
          <img src={resolveImageUrl(images[activeImage])} alt={name} className="max-w-[92vw] max-h-[88vh] object-contain" onClick={(e) => e.stopPropagation()} />
          <button onClick={(e) => { e.stopPropagation(); setActiveImage((i) => (i + 1) % images.length); }} className="absolute right-4 lg:right-8 text-white p-3 hover:bg-white/10 rounded-xl">
            <ChevronRight className="w-7 h-7" />
          </button>
        </div>
      )}
    </div>
  );
};

export default VehicleDetail;
